import { last } from "lodash";
import { Api } from "queries/types";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { DeepPartial, Optional, Required } from "utility-types";
import { get, post, put, patch, remove } from "./http";

interface Payload {
  id: number;
  shop: string;
  name: string;
  isActive: boolean;
  height: number;
  width: number;
  shopGridCells: {
    id: number | null;
    shopFurniture: number | null;
    shopGridCellSections: {
      id: number | null;
      sectionIndex: number;
      shopGridCellSectionInventories: {
        id: number | null;
        quantity: number;
        inventoryIndex: number;
        subFamily: number | null;
      }[];
    }[];
  }[];
}

export type CreatePayload = Optional<Omit<Payload, "id">, "shopGridCells">;

export type UpdatePayload = Required<DeepPartial<Payload>, "id">;

const uriToId = (uri: string) => last(uri.split("/"));

const fetchActiveGrid = async (shopErpId: string): Promise<Api.ShopGrid | null> => {
  const shop = (await get(`/shops/${shopErpId}`)) as Api.Shop;
  const activeGridId = getActiveGridId(shop);
  if (!activeGridId) return null;

  return get(`/shop_grids/${activeGridId}`);
};

const getActiveGrid = async (shop?: Api.Shop): Promise<Api.ShopGrid | null> => {
  if (!shop) return null;
  const activeGridId = getActiveGridId(shop);
  if (!activeGridId) return null;

  return get(`/shop_grids/${activeGridId}`);
};

const getActiveGridId = (shop: Api.Shop): number | undefined => shop.shopGrids.filter((grid) => grid.isActive)[0]?.id;

const useShopGridShow = (id?: number) =>
  useQuery<Api.ShopGrid>(["shops", "grids", id], () => get(`/shop_grids/${id}`), {
    enabled: Boolean(id),
    cacheTime: 0,
  });

const useActiveShopGridByShop = (shop?: Api.Shop) =>
  useQuery<Api.ShopGrid | null>(["shops", shop, "grids"], () => getActiveGrid(shop), { cacheTime: 0, enabled: Boolean(shop) });

const useShopGridCreate = (shop?: Api.Shop) => {
  const queryClient = useQueryClient();

  return useMutation((shopGrid: CreatePayload) => post("/shop_grids", shopGrid), {
    onSuccess: (response) => {
      if (shop)  queryClient.invalidateQueries(["shops", shop.erpId]);
      queryClient.invalidateQueries(["shops", uriToId(response.data.shop), "grids"])
    },
  });
};

const useShopGridUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation((shopGrid: UpdatePayload) => put(`/shop_grids/${shopGrid.id}`, shopGrid), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(["shops", uriToId(response.data.shop), "grids"]);
      queryClient.invalidateQueries(["shops", "grids", response.data.id]);
    },
  });
};

const enableShopGrid = (id: number) => {
  return patch(`/shop_grids/${id}/active`, {});
};

const deleteShopGrid = (id: Api.ShopGrid["id"]) => {
  return remove(`/shop_grids/${id}`)
};

export { useShopGridShow, useShopGridCreate, useShopGridUpdate, fetchActiveGrid, useActiveShopGridByShop, deleteShopGrid, enableShopGrid, getActiveGridId };
